'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';

import { Post } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import { H1 } from '@repo/ui-kit/components/core/Typography';
import { buildEditorUrl } from '@repo/utils/url';

import DesignPostModal from 'components/DesignPost/DesignPostModal/DesignPostModal';
import {
  ComponentContainer,
  ComponentWrapper,
} from 'strapi/component/shared/styles';
import TextBlock from 'strapi/component/shared/TextBlock/TextBlock';
import {
  columnGap,
  FeedWrapper,
} from 'strapi/component/templateFeed/FeedWrapper';
import { FlexColumn } from 'styles/layouts';
import { LayoutSwitch } from 'styles/queries';

import { TextWrapper } from './styles';
import { TemplateFeedProps } from './types';

const TemplateFeed: React.FC<TemplateFeedProps> = ({
  title,
  description,
  templates,
  moreButtonLink,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [columnWidth, setColumnWidth] = useState<number | null>(null);
  const [previewedPost, setPreviewedPost] = useState<Post | null>(null);
  const [clickedPost, setClickedPost] = useState<Post | { id: 'new' } | null>(
    null
  );

  const previousUrl = useRef<string>();
  const pathname = usePathname();

  useEffect(() => {
    if (wrapperRef.current) {
      // we want 2 columns here for mobile
      setColumnWidth(
        Math.min(
          wrapperRef.current?.getBoundingClientRect().width / 2 - columnGap,
          249
        )
      );
    }
  }, []);

  const handlePostDetailsClick = useCallback(
    (post: Post | { id: 'new' }): void => {
      if (post.id !== 'new') {
        // store the previous url
        previousUrl.current = pathname ?? '';
        setPreviewedPost(post as Post);
      } else {
        window.open(buildEditorUrl(), '_self');
      }
    },
    [pathname]
  );

  const handleClose = (): void => {
    const newState = {
      ...window.history.state,
      url: previousUrl.current,
      as: previousUrl.current,
    };
    window.history.pushState(newState, '', previousUrl.current);
    setPreviewedPost(null);
  };

  return (
    <ComponentWrapper>
      <ComponentContainer>
        <FlexColumn ref={wrapperRef} alignItems="center" gap="48px">
          <TextWrapper>
            <H1 bold as="h2" textAlign="center">
              {title}
            </H1>
            <TextBlock text={description} />
          </TextWrapper>
          {columnWidth && (
            <LayoutSwitch
              desktopProps={{ isMobile: false }}
              mobileBreakpoint="largeTabletAndUp"
              mobileProps={{ isMobile: true }}
              width="100%"
            >
              <FeedWrapper
                clickedPost={clickedPost}
                columnWidth={columnWidth}
                handlePostDetailsClick={handlePostDetailsClick}
                isMobile={false}
                moreButtonLink={moreButtonLink}
                setClickedPost={setClickedPost}
                templates={templates}
              />
            </LayoutSwitch>
          )}
          <DesignPostModal post={previewedPost} onClose={handleClose} />
        </FlexColumn>
      </ComponentContainer>
    </ComponentWrapper>
  );
};

export default withEventScope(TemplateFeed, { feature: 'templateFeed' });
